import React, { useCallback, useEffect, useMemo } from "react";
import { useField, useForm } from "react-final-form";
import { Layers as LayersIcon } from "@mui/icons-material";
import { Box, Skeleton, TextField, Typography } from "@mui/material";
import { grey } from "@mui/material/colors";
import { String } from "aws-sdk/clients/acm";
import classNames from "classnames";
import FuzzySet from "fuzzyset";

import { Table, TableBody, TableContainer, TableHead } from "ui";

import { BodyCell, CompactFormControl, HeaderCell, ModifiedTableRow } from "fond/export/ExportTable/ExportTable.styles";
import { ResourceAutocomplete } from "fond/import/modal/ProjectVersionSelection/ProjectVersionSelection";
import { InferredIcon } from "fond/map/Legend";
import { Configuration } from "fond/types";
import { LayerConfig } from "fond/types/ProjectLayerConfig";
import { selectLayersFromConfig } from "fond/utils/configurations";

import { convertLayerMappings, ignoreLayerOption, resolveLayerConfigurationInputLayers, resolveLayerConfigurationNameFromFilename } from "./helper";
import { FormData } from "./SetupForm";

export interface C2SLayersConfiguration {
  physical_elements?: string[] | null;
  network_elements?: string[] | null;
  edge_elements?: string[] | null;
  node_elements?: string[] | null;
  layer_name_mapping?: Record<string, String> | null;
  combine_layers?: Record<string, string[]> | null;
}

interface LayerSelectionStepProps {
  config: Configuration;
}

const LayerSelectionStep: React.FC<LayerSelectionStepProps> = ({ config }) => {
  const { change } = useForm();
  const {
    input: { value: layersConfiguration },
  } = useField<FormData["Inputs"]["ToolLayersConfiguration"]["LayersConfiguration"]["Data"]>(
    "Inputs.ToolLayersConfiguration.LayersConfiguration.Data"
  );

  const {
    meta: { error: stepError, touched, dirty },
    input: { value: layerMappings },
  } = useField<FormData["Inputs"]["ToolLayersConfiguration"]["Mappings"]>("Inputs.ToolLayersConfiguration.Mappings", {
    validate: (value) => {
      if (!value || Object.keys(value).length === 0) {
        return "At least one layer must be mapped.";
      }
      return undefined;
    },
  });

  const layerConfigs = useMemo(() => [ignoreLayerOption, ...selectLayersFromConfig(config, ["LAYER"])] as LayerConfig[], [config]);

  // Get the C2S layers from the configuration
  const c2sLayers = useMemo(() => {
    const layerNames = layersConfiguration ? Array.from(resolveLayerConfigurationInputLayers(layersConfiguration)) : [];
    return layerNames.map((name) => ({ name, displayName: resolveLayerConfigurationNameFromFilename(layersConfiguration!, name) }));
  }, [layersConfiguration]);

  useEffect(() => {
    // Auto-map layers using fuzzy matching only if there are no existing mappings
    if (dirty) return;

    // TODO: extract this logic from LayerImportTable into a reusable function
    const fuzzyset = FuzzySet(c2sLayers.map((layer) => layer.name));

    // Find all matches and order by similarity.
    const matches: [number, string, string][] = [];
    const configsByLabel: { [label: string]: LayerConfig } = {};
    for (const layerConfig of layerConfigs) {
      const label = layerConfig.Label;
      configsByLabel[label] = layerConfig;
      // Exclude matches that are less than 50% similar.
      for (const [similarity, fileKey] of fuzzyset.get(label, [], 0.5)) {
        matches.push([similarity, fileKey, label]);
      }
    }
    matches.sort((prev, next) => next[0] - prev[0]);

    // Get the best match for each label. Each label may only be mapped to one file key.
    const suggestions: { [layerKey: string]: LayerConfig } = {};
    const usedLabels = new Set<string | number>([]);
    for (const [, fileKey, label] of matches) {
      if (!(fileKey in suggestions) && !usedLabels.has(label)) {
        usedLabels.add(label);
        suggestions[fileKey] = configsByLabel[label];
      }
    }

    // Initialize mappings for all C2S layers
    change(
      "Inputs.ToolLayersConfiguration.Mappings",
      convertLayerMappings(Object.fromEntries(c2sLayers.map(({ name: layerName }) => [layerName, suggestions?.[layerName] ?? null])))
    );
  }, [c2sLayers, change, dirty, layerConfigs, layersConfiguration]);

  const handleLayerMappingChange = useCallback(
    (c2sLayerName: string, selectedLayer: LayerConfig | null) => {
      const newValue: FormData["Inputs"]["ToolLayersConfiguration"]["Mappings"] = layerMappings?.filter((m) => m.Name !== c2sLayerName);

      if (selectedLayer?.Key) {
        newValue.push({
          Name: c2sLayerName,
          Layer: {
            LayerKey: selectedLayer.Key,
          },
        });
      }

      change("Inputs.ToolLayersConfiguration.Mappings", newValue);
    },
    [change, layerMappings]
  );

  if (!layersConfiguration) {
    return (
      <Box>
        <Typography variant="h6" gutterBottom>
          Layer Selection
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Please upload a layers configuration first.
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Layer Association
      </Typography>
      <Typography variant="body2" color="text.secondary" paragraph>
        Associate your layers configuration with layers in your project.
      </Typography>

      <Box sx={{ borderRadius: "5px" }}>
        <Box
          height={42}
          // padding={`${theme.spacing(1)} ${theme.spacing(2)}`}
          sx={{
            backgroundColor: grey[100],
            border: `1px solid ${grey[300]}`,
            borderTopLeftRadius: 4,
            borderTopRightRadius: 4,
            borderBottomLeftRadius: 0,
            borderBottomRightRadius: 0,
          }}
          display="flex"
          justifyContent="space-between"
        >
          <Box display="flex" margin="auto 0">
            <LayersIcon
              style={{
                marginRight: 1,
                // fill: theme.palette.biarri.primary.red
              }}
            />
            <Typography variant="h6" component="p">
              {c2sLayers.length} Layers
            </Typography>
          </Box>
        </Box>

        <TableContainer
          // TODO - MOVE TO *.styles
          sx={{
            border: `1px solid ${grey[300]}`,
            borderTopLeftRadius: 0,
            borderTopRightRadius: 0,
            borderBottomLeftRadius: 4,
            borderBottomRightRadius: 4,
            borderTop: "unset",
          }}
        >
          <Table stickyHeader size="small">
            <TableHead>
              <ModifiedTableRow>
                <HeaderCell>
                  Configuration Item{" "}
                  {stepError && touched && (
                    <Typography color="error" component="span">
                      * {stepError}
                    </Typography>
                  )}
                </HeaderCell>
                <HeaderCell width={300}>Select layer</HeaderCell>
              </ModifiedTableRow>
            </TableHead>
            <TableBody className={classNames("customScrollbars")} sx={{ display: "block", overflow: "auto" }}>
              {c2sLayers.map(({ name: c2sLayerName, displayName }) => {
                const mapping = layerMappings.find(({ Name }) => Name === c2sLayerName);
                const value = layerConfigs.find((lc) => lc.Key === mapping?.Layer.LayerKey) || ignoreLayerOption;
                return (
                  <ModifiedTableRow key={c2sLayerName} sx={{ height: "42px" }}>
                    <BodyCell>
                      <Typography variant="body2">{displayName}</Typography>
                    </BodyCell>
                    <BodyCell>
                      <CompactFormControl width={194} size="small">
                        {!config ? (
                          <Skeleton height={42} />
                        ) : (
                          <ResourceAutocomplete
                            sx={{ backgroundColor: "white" }}
                            value={value}
                            onChange={(event, newValue) => handleLayerMappingChange(c2sLayerName, newValue)}
                            options={layerConfigs}
                            getOptionLabel={(option) => option?.Label || ""}
                            renderOption={(option: LayerConfig) => {
                              return (
                                <Box data-testid="option-configuration" display="flex">
                                  {option.ID !== ignoreLayerOption.ID ? (
                                    <Box mr={1} height="22px">
                                      <InferredIcon entity={option} config={config} />
                                    </Box>
                                  ) : (
                                    <Box mr={1} height="22px">
                                      <LayersIcon />
                                    </Box>
                                  )}
                                  <Typography data-testid="option-label">{option.Label}</Typography>
                                </Box>
                              );
                            }}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                InputProps={{
                                  ...params.InputProps,
                                  startAdornment:
                                    value.ID && config ? (
                                      <Box mr={1} height="22px">
                                        <InferredIcon entity={value} config={config} />
                                      </Box>
                                    ) : (
                                      <Box mr={1} height="22px">
                                        <LayersIcon />
                                      </Box>
                                    ),
                                }}
                                placeholder="Select a layer..."
                                variant="outlined"
                              />
                            )}
                          />
                        )}
                      </CompactFormControl>
                    </BodyCell>
                  </ModifiedTableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    </Box>
  );
};

export default LayerSelectionStep;
